/**
 * 통합 리워드 번들 + 던전 밸런스 데이터 (rewards.tsv / dungeonRewards.tsv → generated JSON 재구성).
 * balance.ts와 분리한 던전/리워드 도메인 데이터 모듈 — 소비자(RewardResolver·DungeonController)가 import.
 * 수치는 `data/rewards.tsv` / `data/dungeonRewards.tsv`에서 수정 후 `npm run export:data`로 재생성한다.
 */
import { BigNumber } from "@yjsgames/bignumber";
import dropsJson from "./generated/drops.json";
import rewardsJson from "./generated/rewards.json";
import dungeonRewardsJson from "./generated/dungeonRewards.json";
import type { RewardSpec } from "../models/RewardSpec.js";

/** drops.tsv `table=ref` 의 (id → weight) 가중치 테이블 — randomModule 등급 추첨용 (예: goldBoxRarity). */
function rarityWeightsOf(ref: string): Record<string, number> {
  return Object.fromEntries(
    dropsJson.filter((d) => d.table === ref).map((d) => [d.id, d.weight]),
  );
}

/** rewards.tsv 1행 → RewardSpec. gold/diamond는 BigNumber 직렬화 string으로 정규화. */
function toRewardSpec(row: (typeof rewardsJson)[number]): RewardSpec {
  switch (row.kind) {
    case "gold":
      return { kind: "gold", amount: BigNumber.fromNumber(row.amount).toString() };
    case "diamond":
      return { kind: "diamond", amount: BigNumber.fromNumber(row.amount).toString() };
    case "rebirthPoint":
      return { kind: "rebirthPoint", amount: row.amount };
    case "upgradeStone":
      return { kind: "upgradeStone", amount: row.amount };
    case "randomModule":
      return {
        kind: "randomModule",
        rarityWeights: rarityWeightsOf(row.rarityRef),
        count: Math.max(1, row.modCount),
      };
    case "artifact":
      return { kind: "artifact", artifactId: row.artifactId, amount: row.amount };
    default:
      throw new Error(`[rewardData] unknown reward kind: ${String(row.kind)}`);
  }
}

/**
 * 통합 리워드 번들 (rewards.tsv). bundle ID → RewardSpec[].
 * shopGoldBox = 등급 랜덤 모듈(상점 골드 상자 통합), dungeonCycle{n} = 던전 보상 주기 종류.
 * 던전 보상 수량은 DungeonController.rewardForIndex가 인덱스 비례 공식으로 덮어쓴다.
 */
export const REWARD_BUNDLES: Record<string, RewardSpec[]> = (() => {
  const map: Record<string, RewardSpec[]> = {};
  for (const row of rewardsJson) {
    (map[row.bundle] ??= []).push(toRewardSpec(row));
  }
  return map;
})();

/** 던전 밸런스 (dungeonRewards.tsv) — 일일 도전권 한도 + 보상 주기 + 벽돌/보상 수량 공식 계수. */
const dr = dungeonRewardsJson[0]!;
export const DUNGEON_BALANCE = {
  /** 일일 도전권 한도 (자정 리셋). */
  dailyChallengeLimit: dr.dailyChallengeLimit,
  /** 던전 보상 종류 주기 — index % cyclePeriod 로 dungeonCycle{n} 번들 선택. */
  cyclePeriod: dr.cyclePeriod,
  /** 던전 벽돌 HP = base × growth^index. */
  brickBaseHp: dr.brickBaseHp,
  brickHpGrowth: dr.brickHpGrowth,
  /** 던전 보상 수량 = base × growth^index (종류별). */
  goldBase: dr.goldBase,
  goldGrowth: dr.goldGrowth,
  diamondBase: dr.diamondBase,
  diamondGrowth: dr.diamondGrowth,
  stoneBase: dr.stoneBase,
  stoneGrowth: dr.stoneGrowth,
} as const;
